import type { ReactNode } from "react";

import { useCallback } from "react";

import type {
  AggregateStatus,
  SubIBParams,
  SubIBStats,
  SubIBTableData,
} from "../types";

import { getDefaultDateRange } from "./getDefaultDateRange";
import {
  BaseSubIBReportProvider,
  useBaseSubIBReportContext,
} from "./SubIBReportContext";

export type SubIBReportResources = {
  table: {
    api: (params: SubIBParams) => SubIBTableData | Promise<SubIBTableData>;
    shouldAutoRun: (next: SubIBParams, prev: SubIBParams | null) => boolean;
  };
  stats: {
    api: (params: SubIBParams) => SubIBStats | Promise<SubIBStats>;
    shouldAutoRun: (next: SubIBParams, prev: SubIBParams | null) => boolean;
  };
};

type SubIBReportProviderProps = {
  children: ReactNode;
  resources: SubIBReportResources;
  programOptions: { value: string; label: string }[];
};

const STATUS_OPTIONS: { value: AggregateStatus; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

export const SubIBReportProvider = ({
  children,
  resources,
  programOptions,
}: SubIBReportProviderProps) => {
  const deriveParams = useCallback(
    (state: {
      searchQuery: string;
      dateRange: { from: Date | null; to: Date | null };
      selected: {
        statuses: AggregateStatus[] | null;
        programs: string[] | null;
      };
    }): SubIBParams => ({
      search: state.searchQuery,
      dateRange: state.dateRange,
      selected: state.selected,
    }),
    [],
  );

  return (
    <BaseSubIBReportProvider
      deriveParams={deriveParams}
      resources={{
        table: {
          api: (p) => Promise.resolve(resources.table.api(p)),
          shouldAutoRun: resources.table.shouldAutoRun,
          showLoadingOnRefetch: true,
        },
        stats: {
          api: (p) => Promise.resolve(resources.stats.api(p)),
          shouldAutoRun: resources.stats.shouldAutoRun,
        },
      }}
      selectOptions={{
        statuses: STATUS_OPTIONS,
        programs: programOptions,
      }}
      initialState={{ dateRange: getDefaultDateRange() }}
    >
      {children}
    </BaseSubIBReportProvider>
  );
};

export const useSubIBReportContext = () => useBaseSubIBReportContext();
